'use client';

import type { CarouselApi } from '@polyseme/design-system';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  Icon,
  Typography,
} from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { images } from '~/landing/assets';
import { Button } from '~/landing/components/shared';

const carouselMap = [
  {
    id: 1,
    key: 'slide1' as const,
    imageClassName: 'pr-8',
  },
  {
    id: 2,
    key: 'slide2' as const,
    imageClassName: '',
  },
  {
    id: 3,
    key: 'slide3' as const,
    imageClassName: 'items-center',
  },
] as const;

type DotCarouselProps = {
  goToDemo: () => void;
};

export const DotCarousel = ({ goToDemo }: DotCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const t = useTranslations('landing.germen.simulator.presentation.carousel');

  const autoplay = useRef(Autoplay({ delay: 7000, stopOnInteraction: false }));

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!api) return;

    setScrollSnaps(api.scrollSnapList());
    setSelectedIndex(api.selectedScrollSnap());

    api.on('select', () => {
      setSelectedIndex(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <Carousel
      className="mx-auto w-full max-w-xs overflow-hidden md:max-w-full"
      opts={{
        loop: true,
        align: 'start',
      }}
      orientation={isMobile ? 'horizontal' : 'vertical'}
      plugins={[autoplay.current]}
      setApi={setApi}
    >
      <CarouselContent className="mx-auto -mt-0 flex h-auto md:h-[752px] md:w-full">
        {carouselMap.map((item) => (
          <CarouselItem
            key={item.id}
            className="mr-4 flex h-auto flex-col items-center justify-between gap-6 rounded-3xl bg-foreground pl-8 md:mb-4 md:size-full md:h-full md:basis-full md:flex-row md:gap-0 md:pl-32 md:pr-0"
          >
            <div className="flex flex-1 flex-col justify-start gap-6 pr-8 pt-24 text-left md:w-full md:gap-12">
              <Typography
                as="h3"
                className="text-2xl font-medium text-white md:whitespace-pre-wrap md:text-4xl"
              >
                {t(`${item.key}.description`)}
              </Typography>
              <div>
                <Button
                  className="w-full bg-[#06D578] md:w-auto"
                  size="xl"
                  onClick={goToDemo}
                >
                  {t('cta')}
                  <Icon name="bar-chart" />
                </Button>
              </div>
            </div>
            <div
              className={cn(
                'flex size-full flex-1 items-end justify-center',
                item.imageClassName
              )}
            >
              <Image
                alt={t(`${item.key}.alt`)}
                className="w-full object-contain"
                height={4096}
                src={images.germen[item.key]}
                width={2736}
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="absolute left-1/2 top-4 flex -translate-x-1/2 gap-2 rounded-3xl bg-white/5 p-5 md:left-10 md:top-1/2 md:-translate-x-0 md:-translate-y-1/2 md:flex-col">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            className={`size-2 rounded-full transition-all ${
              index === selectedIndex
                ? 'w-[32px] rounded-3xl bg-[#06D578] md:h-[32px] md:w-2'
                : 'bg-white'
            }`}
            onClick={() => api?.scrollTo(index)}
          />
        ))}
      </div>
    </Carousel>
  );
};
